import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { DropdownMenu } from "@package/subframe/components/DropdownMenu";
import { IconButton } from "@package/subframe/components/IconButton";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { Table } from "@package/subframe/components/Table";
import {
  FeatherMoreHorizontal,
  FeatherPencil,
  FeatherPlus,
  FeatherRotateCcw,
  FeatherSlash,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { CategoryFormDialog } from "@/components/super-admin/CategoryFormDialog";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import { TABLE_MIN_WIDTH } from "@/components/ui/table-scroll-container";
import { useAuth } from "@/contexts/AuthContext";
import {
  useDeactivateCategoryCommandControllerDeactivate,
  useListCategoriesQueryControllerList,
  useUpdateCategoryCommandControllerUpdate,
} from "@/generated/categories/categories";
import type { CategoryResponseDto } from "@/generated/model";
import { CATEGORY_COLORS, type CategoryType } from "@/lib/icons/categories";
import { DEFAULT_PAGE_SIZE } from "@/lib/pagination";

export const Route = createFileRoute(
  "/_authenticated/platform-settings/categories",
)({
  component: PlatformCategoriesPage,
});

/**
 * API colour first, falling back to the static palette for categories seeded
 * before colours existed (pre-NAU-329 environments).
 */
function categoryColor(category: CategoryResponseDto): string | undefined {
  return (
    category.color ??
    CATEGORY_COLORS[category.slug as CategoryType] ??
    undefined
  );
}

function PlatformCategoriesPage() {
  const { userRoleType, isLoading: authLoading } = useAuth();
  const isPlatformAdmin = userRoleType === "PLATFORM_ADMIN";

  const queryClient = useQueryClient();
  const invalidateCategories = () =>
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === "/v1/categories",
    });

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingCategory, setEditingCategory] =
    useState<CategoryResponseDto | null>(null);
  const [deactivatingCategory, setDeactivatingCategory] =
    useState<CategoryResponseDto | null>(null);

  // Gate the fetch on admin so a non-admin never issues the request before the
  // redirect below kicks in.
  const { data: categoriesResponse, isLoading } =
    useListCategoriesQueryControllerList(
      { includeInactive: true },
      { query: { enabled: isPlatformAdmin } },
    );
  const categories = categoriesResponse?.data.items;
  const [page, setPage] = useState(1); // 1-based, matches the Pagination component
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // The endpoint returns the full catalog, so the page is sliced client-side.
  const pagedCategories = useMemo(
    () => (categories ?? []).slice((page - 1) * pageSize, page * pageSize),
    [categories, page, pageSize],
  );

  const deactivateMutation = useDeactivateCategoryCommandControllerDeactivate({
    mutation: { onSuccess: invalidateCategories },
  });
  const updateMutation = useUpdateCategoryCommandControllerUpdate({
    mutation: { onSuccess: invalidateCategories },
  });

  // Wait for auth to resolve before deciding — avoids flashing admin UI to a
  // user who is about to be redirected.
  if (authLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }
  if (!isPlatformAdmin) {
    return <Navigate to="/dashboard" />;
  }

  const handleDeactivate = () => {
    if (!deactivatingCategory) return;
    deactivateMutation.mutate(
      { id: deactivatingCategory.id },
      {
        onSuccess: () => {
          toast.success("Category deactivated");
          setDeactivatingCategory(null);
        },
        onError: () => {
          toast.error("Failed to deactivate category");
        },
      },
    );
  };

  const handleReactivate = (category: CategoryResponseDto) => {
    updateMutation.mutate(
      {
        id: category.id,
        data: {
          name: category.name,
          sortOrder: category.sortOrder,
          isActive: true,
          description: category.description ?? undefined,
          color: category.color ?? undefined,
        },
      },
      {
        onSuccess: () => toast.success("Category reactivated"),
        onError: () => toast.error("Failed to reactivate category"),
      },
    );
  };

  return (
    <div className="flex w-full flex-col items-start gap-8 bg-white animate-fade-in">
      {/* Header */}
      <div className="flex w-full flex-col items-start gap-1">
        <Link
          to="/platform-settings"
          className="text-caption font-caption text-subtext-color hover:text-default-font"
        >
          ← Platform settings
        </Link>
        <div className="flex w-full flex-wrap items-center gap-4">
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
            <span className="w-full text-metrics font-metrics text-primary-navy-800">
              Categories
            </span>
            <span className="text-body font-body text-subtext-color">
              Manage the platform-wide categories. Ordering here drives how they
              appear in pickers.
            </span>
          </div>
          <Button
            variant="brand-primary"
            icon={<FeatherPlus />}
            onClick={() => setIsCreateOpen(true)}
          >
            Create category
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex w-full flex-col items-start gap-8">
        {isLoading ? (
          <div className="flex w-full items-center justify-center py-12">
            <Spinner className="h-8 w-8 text-primary" />
          </div>
        ) : !categories?.length ? (
          <div className="flex w-full flex-col items-center justify-center py-12 gap-4">
            <IconWithLogoBackground variant="neutral" size="x-large" />
            <div className="flex flex-col items-center gap-2">
              <span className="text-heading-2 font-heading-2 text-default-font">
                No categories yet
              </span>
              <span className="text-body font-body text-subtext-color text-center max-w-md">
                Create your first category, or seed the platform defaults from
                the admin tools.
              </span>
            </div>
            <Button
              variant="brand-primary"
              icon={<FeatherPlus />}
              onClick={() => setIsCreateOpen(true)}
            >
              Create category
            </Button>
          </div>
        ) : (
          <div className="flex w-full flex-col items-start bg-default-background overflow-auto">
            <Table
              className={TABLE_MIN_WIDTH.md}
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>Order</Table.HeaderCell>
                  <Table.HeaderCell>Name</Table.HeaderCell>
                  <Table.HeaderCell>Slug</Table.HeaderCell>
                  <Table.HeaderCell>Colour</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell className="items-center justify-end">
                    Actions
                  </Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {pagedCategories.map((category) => {
                const color = categoryColor(category);
                return (
                  <Table.Row
                    key={category.id}
                    className={category.isActive ? "" : "opacity-60"}
                  >
                    <Table.Cell>
                      <span className="text-body font-body text-subtext-color">
                        {category.sortOrder}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {category.name}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-body font-body text-subtext-color">
                        {category.slug}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      {color ? (
                        <div className="flex items-center gap-2">
                          <div
                            className="h-5 w-5 flex-none rounded-md border border-solid border-secondary-beige-200"
                            style={{ backgroundColor: color }}
                          />
                          <span className="text-body font-body text-subtext-color">
                            {color}
                          </span>
                        </div>
                      ) : (
                        <span className="text-body font-body text-subtext-color">
                          —
                        </span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {category.isActive ? (
                        <Badge variant="success">Active</Badge>
                      ) : (
                        <Badge variant="neutral">Inactive</Badge>
                      )}
                    </Table.Cell>
                    <Table.Cell variant="aligned-right">
                      <div className="flex items-center justify-end gap-2">
                        <SubframeCore.DropdownMenu.Root>
                          <SubframeCore.DropdownMenu.Trigger asChild={true}>
                            <IconButton
                              variant="neutral-primary"
                              size="medium"
                              icon={<FeatherMoreHorizontal />}
                            />
                          </SubframeCore.DropdownMenu.Trigger>
                          <SubframeCore.DropdownMenu.Portal>
                            <SubframeCore.DropdownMenu.Content
                              side="bottom"
                              align="end"
                              sideOffset={4}
                              asChild={true}
                            >
                              <DropdownMenu>
                                <DropdownMenu.DropdownItem
                                  icon={<FeatherPencil />}
                                  onClick={() => setEditingCategory(category)}
                                >
                                  Edit
                                </DropdownMenu.DropdownItem>
                                {category.isActive ? (
                                  <DropdownMenu.DropdownItem
                                    icon={<FeatherSlash />}
                                    onClick={() =>
                                      setDeactivatingCategory(category)
                                    }
                                  >
                                    Deactivate
                                  </DropdownMenu.DropdownItem>
                                ) : (
                                  <DropdownMenu.DropdownItem
                                    icon={<FeatherRotateCcw />}
                                    onClick={() => handleReactivate(category)}
                                  >
                                    Reactivate
                                  </DropdownMenu.DropdownItem>
                                )}
                              </DropdownMenu>
                            </SubframeCore.DropdownMenu.Content>
                          </SubframeCore.DropdownMenu.Portal>
                        </SubframeCore.DropdownMenu.Root>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table>

            <Pagination
              className="pt-4"
              page={page}
              pageSize={pageSize}
              totalCount={categories.length}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              itemNoun="categories"
            />
          </div>
        )}
      </div>

      {/* Create dialog */}
      <CategoryFormDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        category={null}
      />

      {/* Edit dialog */}
      <CategoryFormDialog
        open={!!editingCategory}
        onOpenChange={(open) => !open && setEditingCategory(null)}
        category={editingCategory}
      />

      {/* Deactivate confirmation */}
      <Dialog
        open={!!deactivatingCategory}
        onOpenChange={() => setDeactivatingCategory(null)}
      >
        <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
          <DialogTitle className="sr-only">Deactivate category</DialogTitle>
          <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
            <div className="flex max-w-[448px] flex-col items-center gap-6 p-4">
              <div className="flex flex-col items-center gap-6">
                <IconWithLogoBackground
                  variant="warning"
                  size="x-large"
                  icon={<FeatherSlash />}
                />
                <div className="flex flex-col items-center gap-2">
                  <span className="w-full text-heading-2 font-heading-2 text-default-font text-center">
                    Deactivate category?
                  </span>
                  <span className="text-body font-body text-subtext-color text-center">
                    <strong>{deactivatingCategory?.name}</strong> will be hidden
                    from new tasks but kept on existing ones. You can reactivate
                    it at any time.
                  </span>
                </div>
              </div>
            </div>
            <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4">
              <Button
                className="h-14 w-80 flex-none"
                variant="neutral-secondary"
                size="extra-large"
                onClick={() => setDeactivatingCategory(null)}
                disabled={deactivateMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                className="h-14 w-80 flex-none"
                variant="destructive-primary"
                size="extra-large"
                onClick={handleDeactivate}
                loading={deactivateMutation.isPending}
                disabled={deactivateMutation.isPending}
              >
                Yes, deactivate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
